'use client'
import Link from "next/link";
import dynamic from "next/dynamic";
import useMount from "react-use/lib/useMount";
import type { LoggingEvent } from "lib/ga/logEvent";
import { Loading } from "components/Loading";
import { Location, SingleProperty, SingleScheme, Siteplan, PortfolioType, ResourceImageDisplay} from "lib/types";
import { useState, useEffect } from "react";
import ImageDisplay from "./ImageDisplay";
import Breadcrumbs from "./Breadcrumbs";
import Title from "./Title";
import Features from "./Features";
import Description from "./Description";
import UpdatedInfo from "./UpdatedInfo";
import Misrepresentation from "./Misrepresentation";
import Sustainability from "./Sustainability";
import ContactForm from "./ContactForm";
import UpgradeForm from "./UpgradeForm";
import LocalAgentsForm from "./LocalAgentsForm";
import logPageView from "lib/ga/pageView"
import logResultPageView from "lib/ga/resultPageView"
import SchemeSpec from "./SchemeSpec";
import Sponsored from "./Sponsored";
import Sidebar from "./Sidebar";
import AgentContactForm from "./AgentContactForm";
import { checkLoggedIn, getTrustLevel, getPayingClient } from "context/context";
import NoListings from "./NoListings";
import MoreInfo from "./MoreInfo";
import stylesGlobal from './result.module.css';
import Demographics from './Demographics';
import Availability from './Availability';
import WhatsNearby from './WhatsNearby'
import Downloads from './Downloads';
import AvailableProperties from "./AvailableProperties";
const Video = dynamic(() => import("./Video"), {
    loading: () => <Loading />,
    ssr: false,
});


const SiteplanDisplay = dynamic(() => import("./Siteplan"), {
    loading: () => <Loading />,
    ssr: false,
});


const Ads = dynamic(() => import("./ads"), {
    loading: () => <Loading />,
    ssr: true
})

type agents = {
    company_id: number
}

type Feature = {
    value: string,
    label: string
}

type SchemeProps = {
    id: number,
        uri: string,
        sponsored: boolean,
        address: any,
        size: number,
        modified: string,
        features: {
            [key: string]: Feature
        },
        visibility: boolean,
        resources: object,
        activePortfolio: PortfolioType,
        type: string,
        thumbnail_uri: string,
        nearby_places,
        use_class: string,
        for_sale: boolean,
        price: number,
        annual_footfall: number,
        images: ResourceImageDisplay[],
        docType: string,
        category_code: string,
        documents: object,
        agents: agents[],
        address_details: object,
        parent: {thumbnail_uri: string, id: number, url: string, name: string},
        location: {uri: string, name: string},
        website: string,
        seo: object,
        geo_location: object,
        sitePlan: object,
        properties: [],
        owners: agents[],
        scheme_sponsored: boolean,
        isLDC: boolean,
        local_agents: agents[],
        researched_website: string,
        has_bid: boolean,
        paying_client: boolean,
        premium: boolean,
        britishLandLayout: boolean
}

const Result = (props) => {
    const loggedIn = checkLoggedIn();
    const trust = getTrustLevel();
    const payingClient = getPayingClient();
    const {
        id,
        uri,
        sponsored,
        address,
        size,
        modified,
        features = {},
        visibility,
        resources,
        activePortfolio,
        type,
        thumbnail_uri,
        nearby_places,
        use_class,
        for_sale,
        price,
        annual_footfall,
        images,
        docType,
        category_code = '',
        documents = [],
        agents = [],
        address_details,
        parent,
        location,
        website,
        geo_location,
        sitePlan,
        properties,
        owners,
        scheme_sponsored,
        isLDC,
        local_agents,
        researched_website,
        has_bid,
        paying_client = false,
        premium,
        britishLandLayout = false
    } = props;
    const name = props.name || props.line1;
    const available_properties = properties ? properties.filter((r) => r.availability.includes('To Let') || r.availability.includes('Under Offer')) : null;
    const letProperties = properties ? properties.filter((r) => !r.availability.includes('To Let') && !r.availability.includes('Under Offer')) : null;
    const [showAllAgentContacts, setShowAllAgentContacts] = useState(false);
    const rent = props.rent_pa;
    useMount(() => {
        let newLayer: LoggingEvent = {
            url: window.location.pathname + window.location.search,
            result_id: id,
            type: docType,
            category: category_code,
            place: location ? location.name : '',
        };

        if (agents.length) newLayer.agent = agents.map((a) => a.company_id).join();
        if (owners.length) newLayer.owner = owners.map((a) => a.company_id).join();

        if (activePortfolio) newLayer.portfolio_id = activePortfolio.id;

        logPageView(newLayer);
        logResultPageView(newLayer);
    });

    const isPaid = sponsored === true;
    const premiumContent = isPaid || trust === 60 || trust === 100;
    const showLdcBanner = isLDC && (!has_bid && !paying_client);

    const backupImage = () => {
        if (!thumbnail_uri && !parent?.thumbnail_uri) {
            return category_code.toLowerCase() === "sc"
                ? `${process.env.NEXT_PUBLIC_APP_CDN_URL}fallback-images/scheme-sc-fullback.jpeg`
                : process.env.NEXT_PUBLIC_APP_CDN_URL + 'fallback-images/' + (category_code || 'SC').toLowerCase() + "-fullback.jpeg";
        }

        return `${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${thumbnail_uri || parent.thumbnail_uri}`;
    };

    let description = "";

    if (features && features.description) {
        description += features.description.value;
    }
    if (features && features.location) {
        if (description.length > 0) {
            description += `<br><br>`;
        }
        description += features.location.value;
    }

    let showPage = visibility;

    useEffect(() => {
        if (!showPage) {
            let body = document.getElementsByTagName('body');
            if (body[0]) {
                body[0].style.overflow = 'hidden';
            }
        }
    });

    return (
        // <ThemeProvider theme={activePortfolio.styles}>
        <div className={`${stylesGlobal.PropertyPage}`}  >
            {!showPage && (
                <div className={stylesGlobal.unavilableWrapper}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <div className={stylesGlobal.propertyUnavilableMessage}>
                        <h2 className={stylesGlobal.subTitleText}>This Property is currently unavailable</h2>
                        <p className={stylesGlobal.largeText}>
                            This property is currently unavailable, if you would like more infomation please contact our support
                            team
                        </p>
                        {location && location.uri && (
                            <p className={stylesGlobal.largeText}>
                                <Link prefetch={false} href={`/shops-to-rent/${location.uri}`}> For more properties within this area click here</Link>
                            </p>
                        )}
                    </div>
                </div>
            )}

            <Sponsored resultId={id} scheme_sponsored={scheme_sponsored} sponsored={sponsored === true || paying_client === true} portfolio={activePortfolio} type={docType} category={category_code} />

            <Breadcrumbs category={category_code} type={docType} location={location} />

            {sponsored === false && paying_client === false && showPage && (
                <Ads sideBar={false} adsSlot="5496237169" />
            )}

            <Title
                id={id}
                title={name}
                address={address}
                isLDC={isLDC}
                isLoggedIn={loggedIn}
                agents={agents}
                researched_website={researched_website}
                setShowAllAgentContacts={setShowAllAgentContacts}
                showAllAgentContacts={showAllAgentContacts}
                type={docType}
                showPage={showPage}
            />

            <div className={stylesGlobal.pageContainer} id="page-container" style={{ paddingTop: "1rem" }}>
                <div className={`${stylesGlobal.resultContainer} ${sponsored ? stylesGlobal.sponsored : stylesGlobal.notSponsored} ${britishLandLayout ? stylesGlobal.britishLandLayout : ''}`}>

                    <ImageDisplay
                        images={images || []}
                        title={name}
                        fallback={backupImage()}
                        sponsored={sponsored === true}
                        type={type}
                        isPaid={isPaid}
                        portfolioName={activePortfolio?.name}
                        priority={true}
                        id={id}
                    />
                    <Sidebar showPage={showPage} isLoggedIn={loggedIn} active={props} parent={parent} sponsored={sponsored === true} payingClient={payingClient} contactable={showLdcBanner} showAllAgentContacts={showAllAgentContacts} />

                    <Features
                        size={size}
                        use_class={use_class}
                        for_sale={for_sale}
                        price={price}
                        rent={rent}
                        annual_footfall={annual_footfall}
                        is_scheme={true}
                        britishLandLayout={britishLandLayout}
                    />

                    <Description headline={features?.lead?.value}
                    introduction={features?.introduction?.value}
                    description={features?.description?.value}
                    keyPoints={features?.key_points ? features.key_points.value : []}
                    locationDescription={features?.location?.value}
                    detailed_specifications={features.detailed_specifications?.value} property={false}                    
                    />




                    {(isPaid && features.hero_video) && <Video video={features.hero_video} />}

                    {premiumContent && <SchemeSpec features={features} />}

                    {premiumContent && <MoreInfo website={website} />}

                    {premiumContent && <Downloads downloads={documents} britishLandLayout={britishLandLayout} id={id} type={docType} />}


                    {sponsored && <SiteplanDisplay siteplan={sitePlan} properties={properties} portfolio={activePortfolio} id={id} />}

                    {(available_properties && available_properties.length === 0 && sponsored) && <NoListings agents={agents} setShowAllAgentContacts={setShowAllAgentContacts} isLoggedIn={loggedIn} id={id} type={type} />}

                    {(available_properties && available_properties.length > 0) && <AvailableProperties properties={available_properties} portfolio={activePortfolio} scheme_uri={uri} />}

                    {(letProperties && letProperties.length > 0 && sponsored) && <Availability
                        properties={letProperties}
                        type="tenant" area="tenants" title="Tenants"
                        portfolio={activePortfolio} />}

                    {(sponsored && docType == "scheme") && <Demographics features={features} portfolio={activePortfolio} />}


                    {(address && sponsored) && <WhatsNearby addressText={address} nearbyPlaces={nearby_places} geo_location={geo_location} resources={resources} name={name} />}


                    <Sustainability
                        sustainability={features['sustainability']}
                        epc={features['epc_rating']}
                        epc_information={features['epc_note']}
                        breeam_rating={features['breeam_rating']}
                        place_name={address?.place?.name || 'TODO'} />

                    <Misrepresentation />
                    <UpdatedInfo modified={modified} type={type} />
                </div>
            </div>
            <ContactForm agents={agents} name={name} address={address_details} id={id} docType={docType} hasBid={has_bid} premium={premium} />
            <LocalAgentsForm name={name} address={address_details} id={id} docType={docType} local_agents={local_agents || []} />
            <AgentContactForm name={name} address={address_details} id={id} docType={docType} />
            {!isPaid && <UpgradeForm name={name} address={address_details} id={id} type={docType} />}
        </div>
    );
};

// Wrap the component so that we have proper acces to the initialised state
type Props = {
    type: string;
    active: SingleProperty | SingleScheme;
    parent: any;
    siblings: any; // unsure
    children: any; // unsure
    siteplan: Siteplan;
    location: Location;
};
const SchemeIndexWithReducer = (props: Props) => {
    return (
        <Result {...props} />
    );
};

export default SchemeIndexWithReducer;
